import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { SUBTITLE_EXTENSIONS } from "@shared/ingest-phases";
import { narrowAssetLanguage } from "@shared/vod-title";
import { foyerApi, ownerIngestApi, type IngestJobView } from "../../http/foyer";
import { uploadInBlocks } from "../../lib/blockUpload";
import { tokens } from "../../theme";

/**
 * Textning — the subtitle files attached to an ingest (`BE-32` over
 * `CONTENT-21`).
 *
 * ## ⚠️ What this tab is NOT, said on the tab itself
 *
 * It is not "the subtitles of this title", because the catalogue has no such
 * concept. `Media.subtitleLanguages[]` upstream is a list of **labels** on a
 * media that does not exist until a transcode has produced one, and there is no
 * subtitle file store in the Content API at all — the portal ships its files to
 * a separate Azure Function whose URL carries its own key inline in committed
 * source (`OLOF-7`, never to be copied into Foyer).
 *
 * So what a file uploaded here does is ride ALONGSIDE the content to delivery.
 * The tab says that out loud rather than letting somebody infer that uploading
 * a `.vtt` makes a language appear on the title. The alternative the ticket
 * offered — defer the tab entirely — was rejected because the upload path
 * already exists and works; what was missing was only a way to reach it.
 *
 * ## Why it is bound to a JOB and not to the title
 *
 * A subtitle is an input to a transcode, so it attaches to the ingest that is
 * about to run and is refused once that ingest has left `queued`: the
 * transcoder already has its input list, and a file added afterwards would be a
 * language the title claims and the rendition does not carry. With no queued
 * job there is nothing to attach to, and this tab says so instead of offering a
 * button that 409s.
 */

/** What a studio actually ships, in the order they ship it. */
const COMMON_LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "en", label: "English" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

function bytesLabel(bytes: number | null): string {
  if (bytes === null) return "not uploaded";
  if (bytes === 0) return "0 bytes — empty file";
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} kB`;
}

export default function SubtitlesTab({
  jobs,
  onChanged,
  surface = "staff",
}: {
  jobs: IngestJobView[] | null;
  onChanged: () => void | Promise<void>;
  /**
   * 🚨 Which route set to speak to. It defaults to `staff`, and for a while
   * that default was the only behaviour there was.
   *
   * This component hard-imported `foyerApi` and called
   * `/api/foyer/distribution/…`, which carries
   * `requirePermission("canManageDistribution")` — a key `hasPermission`
   * refuses OUTRIGHT to any owner session. So every subtitle upload by an
   * external supplier 403'd, while the "that is all the subtitles (including
   * none at all)" checkbox beside it WAS surface-bound and worked.
   *
   * The result was the worst available shape: the only path forward for a
   * partner was to declare the title has none. A step whose entire
   * justification is that subtitles cannot be added after delivery silently
   * resolved to zero subtitles for every title Nenda did not upload itself.
   *
   * `VodWizardDialog` states the rule this restores — *"the client half of
   * that discipline is `ingestCalls`, which binds the base once so a supplier
   * call cannot be written against a staff URL"* — and every neighbouring call
   * already followed it. This one was simply never given the prop.
   */
  surface?: "staff" | "owner";
}) {
  const api = surface === "owner" ? ownerIngestApi : foyerApi;
  const [language, setLanguage] = useState("sv");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // The job a subtitle can still be attached to. Deliberately `queued` only.
  const queued = jobs?.find((j) => j.phase === "queued") ?? null;
  const latest = jobs?.[0] ?? null;
  const attached = latest?.subtitles ?? [];

  async function upload(file: File) {
    if (!queued) return;
    setError(null);
    const dot = file.name.lastIndexOf(".");
    const extension = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "";
    if (!SUBTITLE_EXTENSIONS.includes(extension)) {
      setError(
        `Only ${SUBTITLE_EXTENSIONS.join(" and ")} files. A container the pipeline has never been run against fails fifteen minutes into a serial farm.`
      );
      return;
    }

    setBusy("Asking storage for somewhere to put it…");
    try {
      const target = await api.ingestSubtitleUrl(queued.id, {
        language,
        extension,
      });
      setBusy(`Uploading ${file.name}…`);
      await uploadInBlocks(target.uploadUrl, file, {
        contentType: extension === "vtt" ? "text/vtt" : "application/x-subrip",
      });
      setBusy("Verifying the upload…");
      await api.ingestSubtitleUploaded(queued.id, target.language);
      if (target.narrowedFrom) {
        // Said out loud: `pt-BR` and `pt` are different files to whoever made
        // them, and a supplier not told will send both.
        setError(
          `Filed as “${target.language}”. The catalogue's language column cannot hold a region, so “${target.narrowedFrom}” was narrowed.`
        );
      }
      await onChanged();
    } catch (e) {
      const err = e as { code?: string; message: string };
      setError(
        err.code === "storage_not_configured"
          ? "There is nowhere to put a subtitle: no delivery container is configured. A file stored where nothing collects it is worse than a refusal, so this is one."
          : err.code === "ingest_already_started"
            ? "That ingest has left the queue. The transcoder already has its input list, so a subtitle added now would be a language the title claims and the rendition does not carry."
            : err.code === "no_bytes_uploaded"
              ? "Nothing arrived. The file is empty, or the upload failed on its last block."
              : err.message
      );
    } finally {
      setBusy(null);
    }
  }

  const narrowed = narrowAssetLanguage(language);

  return (
    <Box data-testid="subtitles-tab">
      <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
        The catalogue stores subtitle <b>labels</b>, not files — a language list
        on a rendition that does not exist until a transcode has made one. A
        file uploaded here travels <b>alongside</b> the content to delivery; it
        does not become a track on the title by itself.
      </Alert>

      {error && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {attached.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
            Attached to the latest ingest
          </Typography>
          <Stack spacing={0.75}>
            {attached.map((s) => (
              <Stack
                key={s.language}
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid={`subtitle-${s.language}`}
              >
                <Chip size="small" variant="outlined" label={s.language} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: s.bytes ? tokens.inkMuted : tokens.warning,
                  }}
                >
                  {bytesLabel(s.bytes)}
                  {s.narrowedFrom ? ` · narrowed from ${s.narrowedFrom}` : ""}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {queued === null ? (
        <Typography
          sx={{ fontSize: 12, color: tokens.inkMuted }}
          data-testid="subtitles-no-queued-job"
        >
          {jobs === null
            ? "Loading…"
            : jobs.length === 0
              ? "Start an ingest on the Video tab first — a subtitle attaches to the transcode that is about to run, not to the title."
              : "The latest ingest has already left the queue, so nothing more can be attached to it. A subtitle added now would be a language the title claims and the rendition does not carry."}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            select
            size="small"
            label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{ minWidth: 180 }}
            helperText={
              narrowed?.narrowedFrom
                ? `Filed as ${narrowed.code}`
                : "The catalogue holds a 2–3 letter code, no region."
            }
            inputProps={{ "data-testid": "subtitle-language" }}
          >
            {COMMON_LANGUAGES.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.label} ({l.code})
              </MenuItem>
            ))}
          </TextField>

          <input
            ref={fileInput}
            type="file"
            hidden
            accept=".srt,.vtt"
            data-testid="subtitle-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={Boolean(busy)}
            onClick={() => fileInput.current?.click()}
            data-testid="subtitle-upload"
          >
            Add .srt or .vtt
          </Button>
        </Stack>
      )}

      {busy && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
          <Typography sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}>
            {busy}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
